import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import 'cap-widget';
import { authAPI } from '../../utils/httpClient';

const CaptchaShell = styled.div`
  min-height: 70px;

  cap-widget {
    display: block;
    width: 100%;
    --cap-widget-width: 100%;
    --cap-widget-height: 58px;
    --cap-border-radius: 4px;
    --cap-font: system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
  }
`;

const CapCaptcha = ({ resetSignal, onTokenChange, onConfigChange, onError, lang = 'en' }) => {
  const widgetRef = useRef(null);
  const [config, setConfig] = useState({ enabled: false, apiEndpoint: '' });
  const [widgetKey, setWidgetKey] = useState(0);

  useEffect(() => {
    let mounted = true;

    const loadConfig = async () => {
      try {
        const response = await authAPI.getCaptchaConfig();
        const data = response?.data || {};
        const nextConfig = {
          enabled: !!data.enabled,
          apiEndpoint: data.api_endpoint || '',
        };
        if (!mounted) return;
        setConfig(nextConfig);
        onConfigChange?.(nextConfig);
      } catch (error) {
        if (!mounted) return;
        onError?.(error);
      }
    };

    loadConfig();

    return () => {
      mounted = false;
    };
  }, [onConfigChange, onError]);

  useEffect(() => {
    onTokenChange?.('');
    setWidgetKey((current) => current + 1);
  }, [resetSignal, onTokenChange]);

  useEffect(() => {
    const widget = widgetRef.current;
    if (!widget) return undefined;

    const handleSolve = (event) => {
      onTokenChange?.(event?.detail?.token || '');
    };
    const handleReset = () => {
      onTokenChange?.('');
    };
    const handleError = (event) => {
      onTokenChange?.('');
      onError?.(event?.detail?.message || 'CAPTCHA verification failed');
    };

    widget.addEventListener('solve', handleSolve);
    widget.addEventListener('reset', handleReset);
    widget.addEventListener('error', handleError);

    return () => {
      widget.removeEventListener('solve', handleSolve);
      widget.removeEventListener('reset', handleReset);
      widget.removeEventListener('error', handleError);
    };
  }, [widgetKey, onError, onTokenChange]);

  if (!config.enabled) {
    return null;
  }

  return (
    <CaptchaShell>
      <cap-widget
        key={widgetKey}
        ref={widgetRef}
        required
        data-cap-api-endpoint={config.apiEndpoint}
        data-cap-i18n-initial-state={lang === 'zh' ? '验证真人操作' : "Verify you're human"}
        data-cap-i18n-verifying-label={lang === 'zh' ? '验证中...' : 'Verifying...'}
        data-cap-i18n-solved-label={lang === 'zh' ? '验证通过' : "You're human"}
        data-cap-i18n-error-label={lang === 'zh' ? '验证失败' : 'Error'}
        data-cap-i18n-required-label={lang === 'zh' ? '请先完成人机验证' : "Please verify you're human"}
        data-cap-i18n-verify-aria-label={lang === 'zh' ? '点击验证真人操作' : "Click to verify you're a human"}
        data-cap-i18n-verifying-aria-label={lang === 'zh' ? '正在验证，请稍候' : 'Verifying, please wait'}
        data-cap-i18n-verified-aria-label={lang === 'zh' ? '已验证' : 'Verified'}
        data-cap-i18n-error-aria-label={lang === 'zh' ? '验证出错，请重试' : 'An error occurred, please try again'}
      />
    </CaptchaShell>
  );
};

export default CapCaptcha;
